import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SubscribersPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('http://127.0.0.1:5000/api/admin/newsletter', {
        withCredentials: true,
      });

      // Handle different response formats
      const data = response.data?.data || response.data?.subscribers || response.data?.newsletter || response.data || [];
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch subscribers');
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'active':
      case 'subscribed':
        return 'bg-green-100 text-green-800';
      case 'unsubscribed':
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  // Use API data if available
  const displaySubscribers = subscribers.length > 0 ? subscribers : [];
  const activeCount = displaySubscribers.filter((s) => {
    const status = s.status?.toLowerCase() || '';
    return status === 'active' || status === 'subscribed';
  }).length;
  const totalCount = displaySubscribers.length;

  return (
    <div>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#03002F] font-cabinet mb-2">
            Subscribers
          </h2>
          <p className="text-[#282727] font-satoshi">
            Manage newsletter and marketing subscribers
          </p>
          <div className="mt-4 flex gap-4">
            <div className="bg-white px-4 py-2 rounded-[8px] shadow">
              <span className="text-sm font-satoshi text-[#282727]">Total: </span>
              <span className="font-bold font-satoshi text-[#03002F]">{totalCount}</span>
            </div>
            <div className="bg-white px-4 py-2 rounded-[8px] shadow">
              <span className="text-sm font-satoshi text-[#282727]">Active: </span>
              <span className="font-bold font-satoshi text-green-600">{activeCount}</span>
            </div>
          </div>
        </div>
        <button
          onClick={fetchSubscribers}
          className="bg-[#5F5CF0] hover:bg-[#4d4bd6] text-white font-satoshi font-bold px-4 py-2 rounded-[8px] transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-[8px] text-sm font-satoshi">
          {error}
        </div>
      )}

      <div className="bg-white rounded-[16px] shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="text-[#282727] font-satoshi">Loading subscribers...</div>
          </div>
        ) : displaySubscribers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-[#282727] font-satoshi">No subscribers found</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#03002F]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Subscribed At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displaySubscribers.map((subscriber, index) => (
                  <tr key={subscriber.id || subscriber._id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      #{subscriber.id || subscriber._id || index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {subscriber.name || subscriber.fullName || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-satoshi text-[#282727]">
                      {subscriber.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {subscriber.phone || subscriber.phoneNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {subscriber.source || subscriber.subscriptionSource || 'Website'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full font-satoshi ${getStatusColor(
                          subscriber.status
                        )}`}
                      >
                        {subscriber.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {formatDate(subscriber.subscribedAt || subscriber.createdAt || subscriber.date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribersPage;

